import { InternalId } from '@edgebox/data-definition-kit';
import { ContentOptimizationStatus, TaskStatus } from '@edgebox/sync-core-data-definitions';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IStartedOptimization } from './checkOptimization.client';
import { TaskGroupProgress as OptimizationProgress } from './taskGroupProgress.utils';

/** How long the Optimize action waits between two reads of the optimization it started. */
const OPTIMIZATION_POLL_INTERVAL_MS = 2_000;

/**
 * How long it waits between two reads while the site holds the request with Sync Core's sentence on it:
 * a site that did not take the request, or that sends the draft in its own time, answers in minutes or
 * hours, not seconds.
 */
const OPTIMIZATION_SITE_POLL_INTERVAL_MS = 30_000;

/** What the Optimize action says when an optimization ended without a draft and names no reason. */
export const OPTIMIZATION_ABORTED_COPY = 'The optimization could not be completed.';

/** The states a task ends in. */
const TASK_END_STATES: readonly TaskStatus[] = [
  TaskStatus.Finished,
  TaskStatus.Skipped,
  TaskStatus.Failed,
  TaskStatus.Aborted,
  TaskStatus.LimitExceeded,
];

/** The end states of the task writing a draft in which it wrote none. */
const TASK_ENDED_WITHOUT_DRAFT: readonly TaskStatus[] = [
  TaskStatus.Skipped,
  TaskStatus.Failed,
  TaskStatus.Aborted,
  TaskStatus.LimitExceeded,
];

/**
 * How far an optimization has got, read off its own status: it is being written while it is
 * generating, and checked once Sync Core's review passed a fixer's output. The task group the create
 * answers with cannot say this: when the site writes the draft, that group is recorded finished the
 * moment the request to the site went out.
 */
function toOptimizationProgress(status: ContentOptimizationStatus): OptimizationProgress {
  if (status === ContentOptimizationStatus.Generating) {
    return { progress: 33, title: 'Optimization in progress', finished: false };
  }

  if (status === ContentOptimizationStatus.SystemReview) {
    return { progress: 66, title: 'Optimization in progress', finished: false };
  }

  return { progress: 100, title: 'Optimization in progress', finished: true };
}

/** Where an optimization an Optimize action started stands, and what the action does with it. */
export interface IOptimizationFollow {
  /** The optimization followed until it has a draft or has ended. */
  optimizationId?: InternalId;
  /** How far it has got, while Sync Core or the site is known to be writing its draft. */
  progress?: OptimizationProgress;
  /** Sync Core's sentence on a request the site holds, in the place of the progress. */
  notice?: string;
  /** Why it ended without a draft, or why it could not be started or read. */
  error?: string;
  setError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setNotice: React.Dispatch<React.SetStateAction<string | undefined>>;
  setProgress: React.Dispatch<React.SetStateAction<OptimizationProgress | undefined>>;
  /** Follows an optimization the create started, from its first status on. */
  follow: (started: IStartedOptimization) => void;
  /** Forgets whatever was followed and said, for a screen that shows another page now. */
  reset: () => void;
}

/**
 * Follows an optimization an Optimize action started until it has a draft or has ended: its progress
 * while its draft is written, Sync Core's sentence while the site holds the request, and why it ended
 * when it wrote none. `onDraftReady` is called once the draft is there.
 */
export function useOptimizationFollow({ api, onDraftReady }: { api?: ISyncCoreApi; onDraftReady?: () => void }): IOptimizationFollow {
  const [error, setError] = useState<string | undefined>(undefined);
  // What Sync Core says about an optimization it handed to the site and no longer waits on here: that
  // the site did not take the request, or that the draft arrives when the site sends it.
  const [notice, setNotice] = useState<string | undefined>(undefined);
  // The optimization the Optimize action started and follows until it has a draft or has ended, and the
  // task that writes its draft, whose failure ends the wait while the optimization still says generating.
  const [optimizationId, setOptimizationId] = useState<InternalId | undefined>(undefined);
  const taskIdRef = useRef<InternalId | undefined>(undefined);
  const [progress, setProgress] = useState<OptimizationProgress | undefined>(undefined);
  // Read when the draft arrives rather than followed as a dependency, so a screen passing a new function
  // on every render does not restart the wait.
  const onDraftReadyRef = useRef(onDraftReady);
  onDraftReadyRef.current = onDraftReady;

  useEffect(() => {
    if (!api || !optimizationId) {
      return;
    }

    let active = true;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    // The state the task writing the draft ended in, once read: it does not change again, so it is not
    // read again.
    let taskEnd: TaskStatus | undefined;

    const endWithout = (message: string) => {
      // The selection stays, so the message shows under it and the person can start again.
      setError(message);
      setNotice(undefined);
      setProgress(undefined);
      setOptimizationId(undefined);
    };

    const refresh = async () => {
      try {
        const optimization = await api.governance.contentOptimizations.item(optimizationId, true);
        if (!active) {
          return;
        }

        if (optimization.status === ContentOptimizationStatus.Aborted) {
          endWithout(optimization.statusReason || OPTIMIZATION_ABORTED_COPY);
          return;
        }

        // Sync Core's own generator runs every selected fixer in one task and passes each output through
        // its review, setting system review each time, so its draft is whole once that task has ended. A
        // task that cannot be read decides nothing; without a task named, the status decides alone.
        const taskId = taskIdRef.current;
        if (taskId && taskEnd === undefined) {
          const task = await api.service.tasks.item(taskId, true).catch(() => undefined);
          if (!active) {
            return;
          }
          if (task && TASK_END_STATES.includes(task.status as TaskStatus)) {
            taskEnd = task.status as TaskStatus;
          }
        }

        // A draft the site wrote is recorded at user review when it arrives; one Sync Core wrote is ready
        // at system review once its task ended.
        const writing = optimization.status === ContentOptimizationStatus.Generating;
        const draftArrived =
          optimization.status === ContentOptimizationStatus.UserReview || (!writing && (!taskId || taskEnd !== undefined));
        if (draftArrived) {
          setNotice(undefined);
          setProgress(undefined);
          setOptimizationId(undefined);
          onDraftReadyRef.current?.();
          return;
        }

        // Still generating after its task ended without a draft: nothing is coming.
        if (writing && taskEnd !== undefined && TASK_ENDED_WITHOUT_DRAFT.includes(taskEnd)) {
          endWithout(optimization.statusReason || OPTIMIZATION_ABORTED_COPY);
          return;
        }

        // Still generating after its task finished: the request went to the site, which sends the draft
        // when it has written it. Sync Core's sentence, when there is one, says why it has not arrived yet
        // — the site did not take the request, or it sends the draft in its own time — and it takes the
        // place of the progress, which would say the draft is being written when the site may not be
        // writing it. The site is then asked about less often, so a draft it sends later still arrives,
        // while the person may start again.
        const siteHoldsRequest = writing && taskEnd === TaskStatus.Finished;
        const siteSentence = siteHoldsRequest ? optimization.statusReason || undefined : undefined;
        setNotice(siteSentence);
        setProgress(siteSentence ? undefined : toOptimizationProgress(optimization.status));
        timeout = setTimeout(
          () => {
            void refresh();
          },
          siteSentence ? OPTIMIZATION_SITE_POLL_INTERVAL_MS : OPTIMIZATION_POLL_INTERVAL_MS
        );
      } catch (error) {
        if (!active) {
          return;
        }

        setError((error as Error)?.message || 'Failed to refresh optimization progress.');
        setOptimizationId(undefined);
        setProgress(undefined);
      }
    };

    void refresh();

    return () => {
      active = false;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [api, optimizationId]);

  const follow = useCallback((started: IStartedOptimization) => {
    taskIdRef.current = started.taskId;
    setNotice(undefined);
    setOptimizationId(started.optimizationId);
    setProgress(toOptimizationProgress(ContentOptimizationStatus.Generating));
  }, []);

  const reset = useCallback(() => {
    setError(undefined);
    setNotice(undefined);
    setOptimizationId(undefined);
    setProgress(undefined);
  }, []);

  return { optimizationId, progress, notice, error, setError, setNotice, setProgress, follow, reset };
}
